import React, { forwardRef, Ref, useImperativeHandle, useState } from 'react';
import { GridApi, IFilterParams } from 'ag-grid-community';
import { IFilterReactComp } from 'ag-grid-react';

import { useEffectExceptOnMount } from '@/hooks/useEffectExceptOnMount.hook';
import { TertiaryButton } from '@/components/common/TertiaryButton/TertiaryButton';
import { SimpleButton } from '@/components/common/SimpleButton/SimpleButton';
import { Checkbox } from '@/components/common/CheckboxTag4/CheckboxTag4';
import styles from './selectable-filter.module.scss';

export type SelectableFilterOption = {
  text: string;
  returnValue: string | number;
};

type SelectableFilterOptionGetter = (api: GridApi) => SelectableFilterOption[];

export interface AgGridSelectableFilterOwnProps<TData extends object = {}> {
  optionsOrGetterFunc: SelectableFilterOption[] | SelectableFilterOptionGetter;
  comparator: (data: TData, selectedOptions: SelectableFilterOption[]) => boolean;
  rerenderOnNewRowsLoaded?: boolean;
}

interface IAgGridSelectableFilterProps extends IFilterParams, AgGridSelectableFilterOwnProps { }

function AgGridSelectableFilter(props: IAgGridSelectableFilterProps, ref: Ref<IFilterReactComp>) {
  const { optionsOrGetterFunc, comparator, api, filterChangedCallback, rerenderOnNewRowsLoaded } = props;
  const [selectedOptions, setSelectedOptions] = useState<SelectableFilterOption[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, forceRefresh] = useState<symbol>();

  const options = typeof optionsOrGetterFunc === 'function' ? optionsOrGetterFunc(api) : optionsOrGetterFunc;

  const isIndeterminate = selectedOptions.length > 0 && selectedOptions.length < options.length;
  const isCheckedAll: boolean | null = isIndeterminate ? null : selectedOptions.length > 0;

  const onChangeCheckbox = (isChecked: boolean, option: SelectableFilterOption) => {
    if (isChecked) {
      setSelectedOptions(selectedOptions.filter((el) => el.returnValue !== option.returnValue));
    }
    else {
      setSelectedOptions((prevState) => [
        ...prevState,
        option
      ]);
    }
  };

  const onSelectAll = () => {
    if (selectedOptions.length < options.length) {
      setSelectedOptions([...options]);
    }
    else {
      setSelectedOptions([]);
    }
  };

  const onSaveFilter = () => {
    api.refreshHeader();
    filterChangedCallback();
  };

  const onResetFilter = () => {
    setSelectedOptions([]);
    api.refreshHeader();
  };

  useEffectExceptOnMount(() => {
    if (selectedOptions.length === 0) {
      filterChangedCallback();
    }
  }, [selectedOptions, api]);

  useImperativeHandle(ref, () => {
    return {
      doesFilterPass({ data }) {
        return comparator(data, selectedOptions);
      },

      isFilterActive() {
        return selectedOptions.length > 0;
      },

      getModel() { },

      setModel() { },

      onNewRowsLoaded() {
        if (rerenderOnNewRowsLoaded) {
          // This version of ag-grid renders custom filter only once and does not rerender it anymore
          // So we need to force component rerender by changing the state
          forceRefresh(Symbol());
        }
      }
    };
  });

  return (
    <div className={styles.filter}>
      <div className={styles.content}>
        {options.length > 0 && (
          <>
            <div className={styles.options}>
              <div className={styles['filter-option']}>
                <Checkbox
                  containerProps={{ className: styles['filter-checkbox'] }}
                  labelProps={{ className: styles['checkbox-label'] }}
                  style={{ width: '100% !important' }}
                  checked={isCheckedAll}
                  className={styles['filter-checkbox']}
                  onChange={onSelectAll}
                >
                  Select All
                </Checkbox>
              </div>

              <div>
                {options.map((option) => {
                  const isChecked = Boolean(selectedOptions?.find((el) => el.returnValue === option.returnValue));

                  return (
                    <div
                      className={styles['filter-option']}
                      key={option.returnValue}
                    >
                      <Checkbox
                        containerProps={{ className: styles['filter-checkbox'] }}
                        labelProps={{ className: styles['checkbox-label'] }}
                        id={`filter-option-${option.returnValue}`}
                        checked={isChecked}
                        onChange={() => onChangeCheckbox(isChecked, option)}
                      >
                        {option.text}
                      </Checkbox>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="d-flex justify-content-between mt-10">
              <SimpleButton
                accent="teal"
                className={styles['filter-button']}
                onClick={onSaveFilter}
              >
                Apply
              </SimpleButton>

              <TertiaryButton
                accent="teal"
                className={styles['filter-button']}
                onClick={onResetFilter}
              >
                Reset
              </TertiaryButton>
            </div>
          </>
        )}

        {options.length === 0 && (
          <div className={styles.message}>
            There are no appropriate<br />options in this column<br />to filter the grid
          </div>
        )}
      </div>
    </div>
  );
}

export default forwardRef(AgGridSelectableFilter);
